import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Sparkles, Phone, Video, Mail, Star, ShieldCheck, Heart, Eye, Clock,
  CheckCircle, ChevronDown, MapPin, Award, Users, MessageCircle, Calendar,
  Zap, Moon, Sun, Flame, Compass, Lock
} from "lucide-react";
import { Button } from "./ui/button";
import { ROBOTS_META_CONTENT } from "../lib/indexing";

const SITE_URL = "https://www.bilan2vie.fr";

const FAQ_ITEMS = [
  {
    q: "Comment se déroule une consultation de voyance à distance ?",
    a: "Tu choisis ta formule (téléphone, visio ou par mail), tu réserves ton créneau en ligne et tu reçois immédiatement la confirmation par email avec les instructions pratiques. Le jour J, on se connecte par téléphone ou en visioconférence via le lien que je t'envoie. La séance dure entre 30 minutes et 2 heures selon le service choisi. Tu peux la suivre depuis n'importe où en France métropolitaine, en Belgique, en Suisse ou au Québec.",
  },
  {
    q: "La voyance à distance est-elle aussi précise qu'en présentiel ?",
    a: "Oui, absolument. L'énergie ne connaît ni les murs ni les distances. Je travaille avec ta date de naissance, ta voix et tes vibrations, ce qui me permet de capter ton énergie aussi clairement qu'en cabinet. De nombreuses personnes choisissent même la distance car elles se sentent plus libres de parler depuis chez elles, dans leur cocon, sans pression extérieure.",
  },
  {
    q: "Quels moyens de paiement acceptes-tu pour les consultations en ligne ?",
    a: "Le paiement se fait de manière sécurisée par carte bancaire au moment de la réservation, via la plateforme Square (CB, Apple Pay, Google Pay). Tes coordonnées bancaires ne sont jamais stockées sur le site. Tu peux aussi régler en plusieurs fois selon les montants. Le paiement valide définitivement ton créneau.",
  },
  {
    q: "Combien de temps à l'avance dois-je réserver ma voyance à distance ?",
    a: "Je recommande de réserver au moins 48 heures à l'avance pour avoir le plus de choix de créneaux. Cela dit, je propose aussi des créneaux à la dernière minute (sous 24h) selon mes disponibilités, parfait quand une question urgente surgit dans ta vie. Mon agenda en ligne se met à jour en temps réel.",
  },
  {
    q: "Puis-je enregistrer ma consultation de voyance ?",
    a: "Oui, c'est même fortement recommandé. Beaucoup de personnes oublient des détails importants après une séance émotionnellement riche. Tu peux enregistrer librement la conversation (téléphone ou visio) pour la réécouter à tête reposée. Je m'engage de mon côté à ne jamais enregistrer ni partager nos échanges.",
  },
  {
    q: "Quelle est la différence entre voyance, guidance et médiumnité ?",
    a: "La voyance permet de voyager dans le temps : remonter dans ton passé et recevoir des prédictions sur ton futur, toujours imprégnées de tes énergies actuelles. La guidance t'apporte des conseils et messages-clés pour mieux appréhender l'avenir et faire tes choix. La médiumnité permet d'entrer en contact avec un défunt ou une entité, ce qui demande une approche différente et plus intime.",
  },
  {
    q: "Tu travailles avec quels supports (tarot, oracles, pendule) ?",
    a: "Je travaille principalement avec le Tarot de Marseille, plusieurs jeux d'oracles (Belline, Lenormand, oracles spirituels modernes), ainsi qu'avec le pendule pour les questions précises. Je combine ces outils avec ma propre intuition et mes ressentis énergétiques. Chaque consultation est unique car je m'adapte à ton énergie du moment et à tes questions.",
  },
  {
    q: "Mes informations personnelles sont-elles confidentielles ?",
    a: "Absolument. Le secret professionnel s'applique à 100% : aucune information, prénom, situation ou question ne sera jamais partagée avec qui que ce soit. Le site est conforme RGPD : tes données sont chiffrées, stockées en Europe, et tu peux demander leur suppression à tout moment.",
  },
  {
    q: "Je n'ai jamais consulté en voyance, comment me préparer ?",
    a: "Aucune préparation particulière n'est nécessaire ! Installe-toi dans un endroit calme où tu ne seras pas dérangé·e. Prépare éventuellement une liste de 2 ou 3 questions précises (amour, travail, famille, projet de vie...). Reste ouvert·e et bienveillant·e avec toi-même : la voyance n'est pas une science exacte, c'est un éclairage qui te laisse toujours libre de tes choix grâce à ton libre-arbitre.",
  },
];

const TESTIMONIALS = [
  { name: "Sophie M.", city: "Lyon", text: "Une consultation par téléphone bluffante. Guillaume a vu des choses que personne ne pouvait deviner. Recommandé à 1000%.", rating: 5 },
  { name: "Marc D.", city: "Paris", text: "J'étais sceptique au départ, j'ai essayé la formule mail. La réponse était d'une justesse incroyable. Merci infiniment.", rating: 5 },
  { name: "Isabelle L.", city: "Bordeaux", text: "Voyance en visio depuis chez moi, dans mon canapé. Très professionnel, humble et précis. Cela m'a vraiment éclairée sur mon avenir.", rating: 5 },
  { name: "Karine R.", city: "Bruxelles", text: "Ma première consultation à distance et certainement pas la dernière. Guillaume a un don indéniable, à l'écoute et bienveillant.", rating: 5 },
];

export default function VoyanceDistancePage() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(0);

  const pageUrl = SITE_URL + "/voyance-a-distance";
  const title = "Voyance à distance — Consultations par téléphone, visio ou email partout en France";
  const description = "Consultation de voyance à distance avec Guillaume, voyant médium reconnu à Montélimar. Voyance par téléphone, visioconférence ou par mail, partout en France, Belgique, Suisse. Tirage de tarot, guidance, prédictions précises. Réservation en ligne sécurisée.";
  const keywords = "voyance à distance, voyance par téléphone, voyance par mail, voyance visio, voyant médium, consultation voyance en ligne, tarot à distance, guidance spirituelle, voyance France, voyance Belgique, voyance Suisse, voyance Drôme, Montélimar voyance";

  // Combined JSON-LD: Service + FAQ + BreadcrumbList
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Voyance à Distance",
      "serviceType": "Consultation de voyance par téléphone, visioconférence et email",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Bilan 2 Vie",
        "telephone": "+33-7-68-48-90-56",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "20 quai du Roubion",
          "addressLocality": "Montélimar",
          "postalCode": "26200",
          "addressRegion": "Drôme",
          "addressCountry": "FR"
        },
        "url": SITE_URL,
        "image": SITE_URL + "/og-image.jpg"
      },
      "areaServed": [
        { "@type": "Country", "name": "France" },
        { "@type": "Country", "name": "Belgique" },
        { "@type": "Country", "name": "Suisse" },
        { "@type": "Country", "name": "Luxembourg" },
        { "@type": "Country", "name": "Canada" }
      ],
      "availableChannel": [
        { "@type": "ServiceChannel", "serviceUrl": SITE_URL + "/booking", "name": "Réservation en ligne 24h/24" },
        { "@type": "ServiceChannel", "servicePhone": "+33-7-68-48-90-56", "name": "Téléphone" }
      ],
      "offers": [
        { "@type": "Offer", "name": "Voyance 30 min par téléphone", "price": "45", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
        { "@type": "Offer", "name": "Voyance 1h par visio", "price": "90", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
        { "@type": "Offer", "name": "Consultation par mail", "price": "45", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ_ITEMS.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a }
      }))
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL + "/" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": SITE_URL + "/services" },
        { "@type": "ListItem", "position": 3, "name": "Voyance à distance", "item": pageUrl }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#1B132B] text-[#F7F3FC]">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content={ROBOTS_META_CONTENT} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={SITE_URL + "/og-image.jpg"} />
        <meta property="og:locale" content="fr_FR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#3A115E]/40 via-[#1B132B] to-[#0A2A35]/40" />
        <div className="absolute top-20 right-10 w-96 h-96 rounded-full bg-[#D926A9]/20 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-[#00E5FF]/15 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6 z-10">
          <nav className="text-xs text-[#B59ED1] mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-[#00E5FF]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-[#00E5FF]">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-[#F7F3FC]">Voyance à distance</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D926A9]/15 border border-[#D926A9]/30 text-[#D926A9] text-xs uppercase tracking-widest mb-5">
                <Sparkles className="w-3.5 h-3.5" /> Voyance partout en France et à l'étranger
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="vad-h1">
                Consultation de <span className="text-[#D926A9]">voyance à distance</span> par téléphone, visio ou email
              </h1>
              <p className="text-[#E2D5F8] text-lg leading-relaxed mb-7 max-w-xl">
                Reçois une guidance précise et bienveillante depuis chez toi, où que tu sois. Spiritualité depuis 2015, professionnel depuis 2021, plus de 2 000 consultations réalisées avec sérieux et confidentialité. Réserve ton créneau en moins de 2 minutes.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button onClick={() => navigate('/booking')} className="gradient-primary px-7 py-6 text-base" data-testid="vad-cta-book">
                  <Calendar className="w-5 h-5 mr-2" /> Réserver ma consultation
                </Button>
                <Button onClick={() => document.getElementById('formules')?.scrollIntoView({behavior:'smooth'})} variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 px-7 py-6 text-base">
                  Voir les formules
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[#B59ED1]">
                <div className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" /> 4,9/5 sur 139 avis</div>
                <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#00E5FF]" /> Paiement 100% sécurisé</div>
                <div className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#00E5FF]" /> Confidentialité totale</div>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Phone className="w-7 h-7 text-[#D926A9]" />, title: "Téléphone", desc: "Voix-à-voix, simple et chaleureux" },
                  { icon: <Video className="w-7 h-7 text-[#00E5FF]" />, title: "Visio", desc: "Face-à-face en ligne via lien sécurisé" },
                  { icon: <Mail className="w-7 h-7 text-[#9D4CDD]" />, title: "Par email", desc: "Réponse écrite sous 5 jours" },
                  { icon: <MessageCircle className="w-7 h-7 text-[#FFD700]" />, title: "Sur-mesure", desc: "Format adapté à ta demande" },
                ].map((c, i) => (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 + 0.3 }}
                    className="p-5 rounded-2xl border border-white/10 bg-[#261B3B]/60 backdrop-blur">
                    <div className="mb-3">{c.icon}</div>
                    <h3 className="font-serif text-lg mb-1">{c.title}</h3>
                    <p className="text-xs text-[#B59ED1]">{c.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-10 border-y border-white/5 bg-[#261B3B]/40">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { num: "5+", label: "années d'expérience" },
            { num: "2 000+", label: "consultations réalisées" },
            { num: "4,9/5", label: "note moyenne (139 avis)" },
            { num: "98%", label: "clients satisfaits" },
          ].map((s, i) => (
            <div key={i}>
              <p className="font-serif text-3xl md:text-4xl text-[#00E5FF] mb-1">{s.num}</p>
              <p className="text-xs uppercase tracking-wider text-[#B59ED1]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* POURQUOI CHOISIR LA VOYANCE A DISTANCE */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D926A9] text-sm uppercase tracking-[0.2em] mb-3 block">Les avantages</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Pourquoi choisir la voyance à distance ?</h2>
            <p className="text-[#B59ED1] max-w-2xl mx-auto">
              Une expérience aussi puissante qu'en cabinet, avec le confort et la liberté que tu mérites.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <MapPin />, title: "Partout en France et à l'étranger", desc: "Consultations possibles depuis n'importe quelle ville française, ainsi qu'en Belgique, Suisse, Luxembourg et Canada. Le décalage horaire n'est pas un frein." },
              { icon: <Heart />, title: "Le confort de chez toi", desc: "Dans ton cocon, dans un fauteuil, en pyjama, sans stress ni déplacement. Tu es plus détendu·e donc plus ouvert·e aux messages reçus." },
              { icon: <Clock />, title: "Créneaux flexibles", desc: "Réservation en ligne 24h/24. Horaires d'ouverture larges (mardi au vendredi 10h-19h, samedi 10h-18h). Possibilité de RDV en soirée selon disponibilité." },
              { icon: <ShieldCheck />, title: "Discrétion absolue", desc: "Personne ne sait que tu consultes. Pas de salle d'attente, pas de regards extérieurs. Ton intimité est totalement préservée." },
              { icon: <Award />, title: "Qualité identique au cabinet", desc: "L'énergie ne dépend pas de la distance. Je capte ta voix, ta date de naissance et tes vibrations avec la même précision qu'en présentiel." },
              { icon: <Sparkles />, title: "Replay possible (audio)", desc: "Tu peux enregistrer la séance pour la réécouter plus tard. Pratique pour ne rien oublier d'une consultation riche en informations." },
            ].map((adv, i) => (
              <motion.article key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="p-6 rounded-2xl bg-[#261B3B] border border-white/10 hover:border-[#D926A9]/30 transition-all">
                <div className="w-11 h-11 rounded-full bg-[#D926A9]/15 flex items-center justify-center mb-4 text-[#D926A9]">
                  {React.cloneElement(adv.icon, { className: "w-5 h-5" })}
                </div>
                <h3 className="font-serif text-xl mb-2">{adv.title}</h3>
                <p className="text-sm text-[#E2D5F8] leading-relaxed">{adv.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* LES FORMULES */}
      <section id="formules" className="py-20 bg-[#261B3B]/40 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-80 h-80 rounded-full bg-[#9D4CDD]/15 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-3 block">Mes formules</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Trois manières de consulter à distance</h2>
            <p className="text-[#B59ED1] max-w-2xl mx-auto">
              Choisis le format qui te correspond. Toutes les consultations à distance sont prépayées en ligne pour valider ton créneau.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Phone className="w-7 h-7" />,
                color: "#D926A9",
                title: "Voyance par téléphone",
                price: "Dès 45 €",
                duration: "30 min ou 1h",
                features: [
                  "Échange voix-à-voix en direct",
                  "Réponse immédiate à tes questions",
                  "Idéal si tu n'aimes pas la visio",
                  "Lien d'appel fourni par email",
                  "Possible depuis n'importe où",
                ],
                cta: "Réserver une voyance téléphonique",
              },
              {
                icon: <Video className="w-7 h-7" />,
                color: "#00E5FF",
                title: "Voyance en visioconférence",
                price: "Dès 45 €",
                duration: "45 € / 30 min — 70 € / 1h",
                featured: true,
                features: [
                  "WhatsApp, FaceTime, Instagram, Messenger…",
                  "Tirage de cartes partagé à l'écran",
                  "Plus immersif que le téléphone",
                  "Lien sécurisé envoyé par email",
                  "Conseil : prends des notes pendant la séance",
                ],
                cta: "Réserver une voyance en visio",
              },
              {
                icon: <Mail className="w-7 h-7" />,
                color: "#9D4CDD",
                title: "Consultation par email",
                price: "55 €",
                duration: "Réponse sous 5j",
                features: [
                  "Tu poses tes questions par écrit",
                  "Tu reçois une réponse détaillée par email",
                  "Format pratique si tu manques de temps",
                  "Aucun rendez-vous à caler",
                  "Document conservé à vie",
                ],
                cta: "Demander une consultation par mail",
              },
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className={`p-7 rounded-2xl bg-[#1B132B] border ${f.featured ? 'border-[#00E5FF]/50 shadow-[0_0_40px_rgba(0,229,255,0.1)]' : 'border-white/10'} relative`}>
                {f.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#00E5FF] text-[#1B132B] text-[10px] font-bold uppercase tracking-widest">
                    Le plus choisi
                  </span>
                )}
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: f.color + '20', color: f.color }}>
                  {f.icon}
                </div>
                <h3 className="font-serif text-2xl mb-2">{f.title}</h3>
                <div className="flex items-baseline gap-3 mb-1">
                  <p className="text-3xl font-serif" style={{ color: f.color }}>{f.price}</p>
                  <span className="text-xs text-[#B59ED1]">{f.duration}</span>
                </div>
                <ul className="space-y-2 my-5">
                  {f.features.map((feat, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#E2D5F8]">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: f.color }} />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/booking')} className="w-full gradient-primary mt-3" data-testid={`vad-formule-${i}-cta`}>
                  {f.cta}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT CA MARCHE */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D926A9] text-sm uppercase tracking-[0.2em] mb-3 block">Étape par étape</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Comment se déroule une consultation à distance ?</h2>
          </div>

          <ol className="grid md:grid-cols-4 gap-6 relative">
            {[
              { num: "01", icon: <Calendar />, title: "Tu réserves en ligne", desc: "Choisis ta formule, ta date et ton créneau. Paiement sécurisé immédiat." },
              { num: "02", icon: <Mail />, title: "Tu reçois ta confirmation", desc: "Email avec instructions pratiques, lien visio (si applicable) et créneau confirmé." },
              { num: "03", icon: <Eye />, title: "On se connecte ensemble", desc: "Le jour J, on échange par téléphone ou visio, dans un cadre intime et confidentiel." },
              { num: "04", icon: <Heart />, title: "Tu repars éclairé·e", desc: "Tu peux enregistrer la séance, prendre des notes et appliquer les conseils reçus." },
            ].map((step, i) => (
              <li key={i} className="relative p-6 rounded-2xl bg-[#261B3B] border border-white/10">
                <span className="font-serif text-5xl text-[#D926A9]/20 absolute top-3 right-4">{step.num}</span>
                <div className="w-10 h-10 rounded-full bg-[#D926A9]/15 flex items-center justify-center text-[#D926A9] mb-3">
                  {React.cloneElement(step.icon, { className: "w-5 h-5" })}
                </div>
                <h3 className="font-serif text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-[#E2D5F8] leading-relaxed">{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* DOMAINES ABORDES */}
      <section className="py-20 bg-[#261B3B]/40">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-3 block">Tous les sujets de la vie</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Quels sujets aborder en voyance à distance ?</h2>
            <p className="text-[#B59ED1] max-w-2xl mx-auto">
              Aucune question n'est trop intime ni trop banale. Pose-moi ce qui te préoccupe vraiment, sans filtre.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <Heart />, label: "Amour & couple" },
              { icon: <Users />, label: "Famille & enfants" },
              { icon: <Compass />, label: "Travail & carrière" },
              { icon: <Sun />, label: "Projets de vie" },
              { icon: <Moon />, label: "Spiritualité" },
              { icon: <Flame />, label: "Décisions importantes" },
              { icon: <Zap />, label: "Choix professionnels" },
              { icon: <Sparkles />, label: "Chemin de vie" },
            ].map((d, i) => (
              <div key={i} className="p-4 rounded-xl bg-[#1B132B] border border-white/10 hover:border-[#D926A9]/30 transition-all text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-[#D926A9]/15 flex items-center justify-center text-[#D926A9] mb-2">
                  {React.cloneElement(d.icon, { className: "w-5 h-5" })}
                </div>
                <p className="text-sm text-[#E2D5F8]">{d.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D926A9] text-sm uppercase tracking-[0.2em] mb-3 block">Ils ont consulté à distance</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Ce qu'en disent mes consultants</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.article key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="p-6 rounded-2xl bg-[#261B3B] border border-white/10">
                <div className="flex items-center mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                  ))}
                </div>
                <p className="text-[#E2D5F8] italic leading-relaxed mb-4">« {t.text} »</p>
                <p className="text-sm text-[#B59ED1]">— {t.name}, {t.city}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#261B3B]/40">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#00E5FF] text-sm uppercase tracking-[0.2em] mb-3 block">Questions fréquentes</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">FAQ — Voyance à distance</h2>
            <p className="text-[#B59ED1]">
              Tu hésites encore ? Voici les 10 questions que les consultants me posent le plus souvent.
            </p>
          </div>

          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="rounded-xl bg-[#1B132B] border border-white/10 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
                  aria-expanded={openFAQ === i}
                  className="w-full p-5 flex justify-between items-start gap-4 text-left hover:bg-[#D926A9]/5 transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-serif text-base md:text-lg pr-4">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 text-[#D926A9] transition-transform ${openFAQ === i ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === i && (
                  <div className="px-5 pb-5 text-sm text-[#E2D5F8] leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D926A9]/20 via-[#1B132B] to-[#00E5FF]/15" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D926A9]/20 blur-3xl" />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Sparkles className="w-12 h-12 text-[#D926A9] mx-auto mb-4" />
          <h2 className="font-serif text-3xl md:text-5xl mb-4 leading-tight">
            Prêt·e à recevoir des réponses claires sur ton avenir ?
          </h2>
          <p className="text-[#E2D5F8] text-lg mb-8 max-w-xl mx-auto">
            Réserve ta consultation de voyance à distance dès maintenant et obtiens les éclairages dont tu as besoin pour avancer sereinement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => navigate('/booking')} className="gradient-primary px-8 py-6 text-base" data-testid="vad-cta-final">
              <Calendar className="w-5 h-5 mr-2" /> Réserver ma voyance à distance
            </Button>
            <Button onClick={() => navigate('/services')} variant="outline" className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-base">
              Découvrir toutes mes prestations
            </Button>
          </div>
          <p className="text-xs text-[#B59ED1] mt-6">Réservation 24h/24 · Paiement sécurisé · Confidentialité garantie</p>
        </div>
      </section>
    </div>
  );
}
